import { createMap, OmniaPromise } from "../../omnia"
import { PlanState } from "../types"

export function setupObservations(observations: { text: string, actions: string[] }[]): PlanState["userObservations"] {

    return (observations ?? []).map(o => ({

        key: getKey(o.text),
        text: o.text,

        actions: (o.actions ?? []).map(a => ({
            key: getKey(a),
            text: a
        }))

    }))

}

export interface ObservationsMgr {

    readonly minimumLength: number

    isActive(observation: number, action?: number): boolean
    isUserDefined(observation: string, action: string): boolean

    save(payload: { observation: string, action?: string, save: boolean }): OmniaPromise<any>

    getEntries(action?: string): string[]

}

type UserObservations = PlanState["userObservations"]
type UserObservation = UserObservations[0]


export function createObservationsMgr(state: UserObservations, active: { observation: number, action?: number }, save: ObservationsMgr["save"]): ObservationsMgr {

    const current = state
        .map(s => ({
            ...s,
            actions: (s.actions ?? []).sort((a, b) => a.text?.localeCompare(b.text)),
            actionsMap: createMap(s.actions, a => a.key)
        }))
        .sort((a, b) => a.text?.localeCompare(b.text))

    const map = createMap(current, s => s.key)


    function isActive(observation: number, action?: number) {
        return active && (active.observation === observation) && (active.action == action)
    }

    function isUserDefined(observation: string, action?: string) {

        const obs = map.get(getKey(observation))

        if (!obs) {
            return false
        }

        const actionKey = getKey(action)

        return actionKey
            ? obs.actionsMap.has(actionKey)
            : true


    }


    function getEntries(observation?: string) {

        if (!observation) {
            return current.map(c => c.text)
        }
        else {
            const obs = map.get(getKey(observation))
            return obs?.actions?.map(a => a.text) ?? []
        }

    }

    return {

        minimumLength: 3,

        save,

        isActive,
        isUserDefined,
        getEntries

    }
}


interface Save {

    /** Observation (required) */
    observation: string

    /** Action (undefined if this is an observation, required if this is an action) */
    action?: string

    /** Indicates if this observation/action should be saved */
    save: boolean

}

export function saveUserObservation(state: UserObservations, { observation, action, save }: Save) {

    const observationKey = getKey(observation)

    if (!observationKey) {
        return state
    }

    const actionKey = getKey(action)

    if ((action !== undefined) && (!actionKey)) {
        return state
    }

    let matchingObservation = state.find(s => s.key === observationKey)

    if (!(matchingObservation || save)) {
        return state
    }

    state = state.filter(o => o !== matchingObservation)

    if ((!save) && (!actionKey)) {
        return state
    }

    matchingObservation = matchingObservation ?? {
        key: observationKey,
        text: observation,
        actions: []
    }

    if (actionKey) {
        matchingObservation = setAction(matchingObservation, actionKey, action, save)
    }

    state = [matchingObservation].concat(state)

    return state


}

function setAction(obs: UserObservation, key: string, text: string, save: boolean) {

    const actions = obs.actions.filter(a => a.key !== key)

    if (save) {
        actions.push({ key, text })
    }

    return { ...obs, actions }

}

function getKey(text: string) {
    const key = (text ?? "").toLocaleLowerCase().trim().replace(/ +/, " ")
    return key ? key : undefined
}
